import { reactive } from 'vue';
import { API_VERSION } from '@vp/module-runtime';
import { DATA_FORMAT_VERSION } from '@vp/core';
import { request } from './catalog';
import { t } from './i18n';
import { storage } from './platform';
import { pwa } from './pwa';
import { storagePlace } from './storage-location';

/**
 * "Report a problem": the admin sets the recipient's address; the person writes a message and the
 * app opens their own mail program with the letter ready (they see all of it and send it themselves —
 * nothing goes through our server). Technical details (version, browser, the codes of recent errors)
 * are attached only if the person leaves the box ticked, and are shown in full before sending. Without
 * an address the button is not shown.
 */
export type FeedbackKind = 'bug' | 'idea' | 'question';

export const feedback = reactive({ email: null as string | null, open: false });

const KEY = 'feedback.email';

/** The address from the server; the last known one when offline (the button must not vanish on a train). */
export async function loadFeedback(): Promise<void> {
  try {
    const saved = await storage.getSetting<string | null>(KEY);
    if (typeof saved === 'string') feedback.email = saved;
  } catch {
    /* no storage yet */
  }
  try {
    const { res } = await request('/api/v1/feedback');
    if (!res.ok) return;
    const email = ((await res.json()) as { email: string | null }).email;
    feedback.email = email;
    await storage.setSetting(KEY, email).catch(() => undefined);
  } catch {
    /* offline or no server */
  }
}

/** Version, device and the last few warning/error codes: no canvas content, no names. */
export async function technicalInfo(): Promise<string> {
  const lines = [
    `${t('app.title')} ${__APP_VERSION__} (${__BUILD_TYPE__}, ${__BUILD_NUMBER__ ? '#' + __BUILD_NUMBER__ : t('about.buildLocal')}, ${__BUILD_ID__ === 'dev' ? 'dev' : __BUILD_ID__.slice(0, 10)})`,
    `API ${API_VERSION}, ${t('about.format')} ${DATA_FORMAT_VERSION}`,
    `${navigator.userAgent}`,
    `${navigator.language}, ${window.innerWidth}×${window.innerHeight}`,
    `${storagePlace.kind === 'folder' ? 'folder' : 'browser'} storage, ${pwa.standalone ? 'installed app' : 'browser tab'}, ${pwa.online ? 'online' : 'offline'}`,
  ];
  try {
    const rows = (await storage.queryLogs({ limit: 60 })).filter((r) => r.level === 'error' || r.level === 'warn').slice(0, 8);
    if (rows.length) lines.push('', ...rows.map((r) => `${new Date(r.ts).toISOString()} ${r.level} ${r.code}`));
  } catch {
    /* no journal */
  }
  return lines.join('\n');
}

const SUBJECT: Record<FeedbackKind, string> = { bug: 'feedback.kind.bug', idea: 'feedback.kind.idea', question: 'feedback.kind.question' };
/** Many mail programs cut a mailto: link that is longer than this. */
const MAX_HREF = 1900;

export interface Mail {
  /** The whole letter as the person sees it. */
  text: string;
  subject: string;
  href: string;
  /** The link had to be cut: "Copy" gives the complete text. */
  cut: boolean;
}

export function buildMail(to: string, kind: FeedbackKind, message: string, info: string | null, topic = ''): Mail {
  const addr = to.trim();
  const kindLabel = t(SUBJECT[kind]);
  const subject = topic.trim() ? `${t('app.title')}: ${kindLabel} — ${topic.trim()}` : `${t('app.title')}: ${kindLabel}`;
  const text = info ? `${message.trim()}\n\n---\n${info}` : message.trim();
  // The address stays in front of "?". Line breaks are CRLF: with a bare LF, Windows
  // mail programs open the letter and leave the address out.
  const link = (body: string) => `mailto:${addr}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body.replace(/\r?\n/g, '\r\n'))}`;
  let href = link(text);
  let cut = false;
  if (href.length > MAX_HREF) {
    cut = true;
    let body = text;
    while (body.length > 20 && link(body + '…').length > MAX_HREF) body = body.slice(0, Math.floor(body.length * 0.9));
    href = link(body + '…');
  }
  return { text, subject, href, cut };
}
